import moment from 'moment-timezone';
import { Resource } from '../../../common/constants/resource';

export const NORA_API_DATE_FORMAT = 'YYYY-MM-DD';
export const NORA_DISPLAY_DATE_FORMAT = 'MM/DD/YYYY';
export const DATE_FORMAT_ONLY_MONTH_AND_YEAR = 'MMM YYYY';

export const dateToString = (
  date?: string,
  displayFormat: string = NORA_DISPLAY_DATE_FORMAT,
) => {
  if (!date) {
    return Resource.NORA_M_DASH;
  }
  if (moment(date, NORA_API_DATE_FORMAT, true).isValid()) {
    return moment(date, NORA_API_DATE_FORMAT).format(displayFormat);
  }
  return date;
};

export const capitalizeFirstLetter = (value: string) => {
  if (!value) {
    return value;
  }
  return value[0].toUpperCase() + value.slice(1);
};

export const booleanToString = (value: boolean) => {
  return capitalizeFirstLetter(valueToString(value));
};

export const valueToString = (value: boolean | number | string | undefined) => {
  return value !== undefined ? String(value) : Resource.NORA_M_DASH;
};

export const calculateAgeFromDob = (date: string) => {
  return moment().diff(moment(date, NORA_API_DATE_FORMAT), 'years');
};

export const formatInsuredAge = (birthDate: string): string => {
  const dob = moment(birthDate, NORA_API_DATE_FORMAT);
  const now = moment();
  const days = now.diff(dob, 'days');
  const months = now.diff(dob, 'months');
  const years = now.diff(dob, 'years');
  if (years >= 1) return years === 1 ? '1 year' : `${years} years`;
  if (months >= 1) return months === 1 ? '1 month' : `${months} months`;
  return days === 1 ? '1 day' : `${days} days`;
};

export const genderShortFormToLongForm = (gender: string) => {
  switch (gender.toLowerCase()) {
    case 'f':
      return Resource.GENDER_FEMALE;
    case 'm':
      return Resource.GENDER_MALE;
    default:
      return;
  }
};

export const booleanToYesNo = (value: boolean | undefined | null) => {
  if (value === undefined || value === null) {
    return Resource.NORA_M_DASH;
  } else {
    return value ? Resource.YES_STRING : Resource.NO_STRING;
  }
};

export const numberToOrdinal = (value: number): string | undefined => {
  if (value < 0) {
    return undefined;
  }
  if (value === 0) {
    return '0th';
  }
  const postFix =
    value < 11 || value > 13
      ? ['st', 'nd', 'rd', 'th'][Math.min((value - 1) % 10, 3)]
      : 'th';
  return `${value}${postFix}`;
};

/**
 * Returns the age to display, preferring underwriting age (age) over nearest age (nearestAge).
 * Underwriting age is the fixed age used for policy calculations.
 * @param age - Underwriting age (age last birthday)
 * @param nearestAge - Nearest age (Age Nearest Birthday, calculated dynamically)
 * @param birthDate - ISO date string; used to compute days/months when age < 1
 * @returns Formatted age string with units, or Resource.NORA_M_DASH if neither is valid
 */
export const getAge = (age: number, nearestAge: number, birthDate?: string): string => {
  const resolvedAge = age != null && age >= 0 && Number.isFinite(Number(age))
    ? Number(age)
    : nearestAge != null && nearestAge >= 0 && Number.isFinite(Number(nearestAge))
      ? Number(nearestAge)
      : null;

  if (resolvedAge === null) {
    return Resource.NORA_M_DASH;
  }

  if (resolvedAge === 0 && birthDate) {
    return getAgeUnderOneYear(birthDate);
  }

  return `${resolvedAge} yr`;
};

const getAgeUnderOneYear = (birthDate: string): string => {
  const birth = moment(birthDate, NORA_API_DATE_FORMAT);
  const today = moment();
  const months = today.diff(birth, 'months');
  if (months === 0) {
    const days = today.diff(birth, 'days');
    return `${days} ${days === 1 ? 'day' : 'days'}`;
  }
  const monthLabel = `${months} ${months === 1 ? 'month' : 'months'}`;
  const remainingDays = today.diff(birth.clone().add(months, 'months'), 'days');
  if (remainingDays === 0) {
    return monthLabel;
  }
  return `${monthLabel}, ${remainingDays} ${remainingDays === 1 ? 'day' : 'days'}`;
};
